import React, { useState } from "react";
import { MUITable, PaginationComponent } from "../../common/components";
import {
  Card,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from "@mui/material";
import CardCover from "@mui/joy/CardCover";
import "./guides-list.scss";
import playIcons from "../../assets/icons/playIcons.svg";
import ViewGuidesVideo from "./view-guides-video/view-guides-video";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AttachmentActions from "./text-guide-attachment-info/text-guide-attachment-info";
import FilePreviewModal from "./text-guide-attachment-info/file-preview-modal";
import { getIconBasedOnFileExt } from "../../utils/fetchIconForExtension";
import CustomTooltip from "../../common/components/custom-tooltip";
import CircularProgress from "@mui/material/CircularProgress";

interface GuidesListProps {
  mediaType: "Video" | "Text";
  searchText?: string;
  selectedCategory?: string;
  setIsLoading: (loading: boolean) => void;
  totalRecords: number;
  setTotalRecords: (total: number) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  data: any[];
  setData?: (data: any[]) => void;
  pageSize: number;
  fetchGuides: () => void;
  isSuperTenant?: boolean;
  setAlertMessage: (message: string) => void;
  setAlertOpen: (open: boolean) => void;
  setAlertType: (type: "success" | "error") => void;
}
interface LazyThumbnailProps {
  src: string;
  alt: string;
  onClick: () => void;
  className?: string;
  id?: string | number;
}

const LazyThumbnail: React.FC<LazyThumbnailProps> = ({
  src,
  alt,
  onClick,
  className,
  id,
}) => {
  const hasThumbnail = Boolean(src && src.trim() !== "");
  const [loading, setLoading] = useState(hasThumbnail);
  const [error, setError] = useState(false);

  return (
    <div className="thumbnail-wrapper">
      {/* Loader centered */}
      {loading && !error && hasThumbnail && (
        <div className="thumbnail-loader">
          <CircularProgress size={34} />
        </div>
      )}

      {!error && hasThumbnail ? (
        <img
          src={src}
          alt={alt}
          key={id}
          className={`thumbnail-img ${loading ? "hidden" : ""} ${className || ""}`}
          loading="lazy"
          onLoad={() => {
            setLoading(false);
          }}
          onError={() => {
            setLoading(false);
            setError(true);
          }}
          onClick={onClick}
        />
      ) : (
        <div className="thumbnail-fallback" onClick={onClick}>
          <img src={playIcons} alt="Play" className="fallback-icon" />
        </div>
      )}
    </div>
  );
};


const GuidesList: React.FC<GuidesListProps> = ({
  mediaType,
  searchText,
  selectedCategory,
  setIsLoading,
  totalRecords,
  setTotalRecords,
  currentPage,
  setCurrentPage,
  data,
  pageSize,
  fetchGuides,
  isSuperTenant,
  setAlertMessage,
  setAlertOpen,
  setAlertType,
}) => {
  const [showVideo, setShowVideo] = useState<any>({
    isShowVideo: false,
    label: "",
    url: "",
    thumbnail: "",
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuIndex, setMenuIndex] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedFileForPreview, setSelectedFileForPreview] =
    useState<any>(null);

  const handleMenuOpen = (
    e: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    setAnchorEl(e.currentTarget);
    setMenuIndex(index);
  };
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuIndex(null);
  };

  const renderVideoGuide = (guide: any) => (
    <div className="guide-video-card-container">
      <div className="guide-video-card-wrapper">
        <div className="guide-video-card">
          <LazyThumbnail
            src={guide.thumbnail}
            alt={guide.title}
            className="guide-thumbnail"
            onClick={() =>
              setShowVideo({
                isShowVideo: true,
                label: guide.category,
                url: guide.url,
                thumbnail: guide.thumbnail,
              })
            }
            id={guide?.id}
          />
        </div>
      </div>
      <div className="guide-video-card-info">
        <div className="guide-video-card-info-label-duration">
          <CustomTooltip title={guide?.title} placement="top" offsetX={18}>
            <span className="guide-video-card-info-label">{guide?.title}</span>
          </CustomTooltip>
          <div className="guide-video-card-info-duration">
            {guide?.category}
          </div>
        </div>
        <div className="guide-video-card-info-icon">
          <img
            src={playIcons}
            alt="Close"
            onClick={() => {
              setShowVideo({
                isShowVideo: true,
                label: guide?.category,
                url: guide?.url,
                thumbnail: guide?.thumbnail,
              });
            }}
          />
        </div>
      </div>
    </div>
  );

  const textGuideListHeaders = [
    { key: "title", label: "Title", isSortable: false },
    { key: "category", label: "Category", isSortable: false },
    { key: "mediaType", label: "Guide Type", isSortable: false },
    { key: "uploadedBy", label: "Uploaded by", isSortable: false },
    { key: "uploadedTime", label: "Uploaded Date & Time", isSortable: false },
    {
      key: "actions",
      label: "Actions",
      isSortable: false,
    },
  ];

  const handleRowClick = (item: any) => {
    setSelectedFileForPreview(item);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedFileForPreview(null);
  };

  const renderTextGuideTable = (textGuidesData: any[]) => {
    return (
      <MUITable
        headers={textGuideListHeaders}
        data={textGuidesData.map((item, index) => ({
          ...item,
          actions: (
            <div onClick={(e) => e.stopPropagation()}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e, index);
                }}
                size="small"
                title=""
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <AttachmentActions
                open={menuIndex === index}
                anchorEl={anchorEl}
                onClose={handleMenuClose}
                type="FILE"
                item={item}
                fetchGuidesData={fetchGuides}
                setLoading={setIsLoading}
                setAlertMessage={setAlertMessage}
                setAlertOpen={setAlertOpen}
                setAlertType={setAlertType}
                disableEdit={false}
                isSuperTenant={isSuperTenant}
              />
            </div>
          ),
        }))}
        noResultsTxt={
          "No guides found for your search. Try different keywords."
        }
        onRowClick={handleRowClick}
      />
    );
  };

  return (
    <>
      <div className="guides-list-container">
        {data.length > 0 ? (
          mediaType === "Video" ? (
            <div className="guides-grid">
              {data.map((guide) => renderVideoGuide(guide))}
            </div>
          ) : (
            <div className="guides-table-container">
              {renderTextGuideTable(data)}
            </div>
          )
        ) : (
          <div className="no-guides-message">
            {searchText
              ? "No guides found for your search. Try different keywords."
              : `No ${mediaType.toLowerCase()} guides found`}
          </div>
        )}
      </div>
      {showVideo?.isShowVideo ? (
        <ViewGuidesVideo
          onClose={() =>
            setShowVideo({
              isShowVideo: !showVideo?.isShowVideo,
              label: "",
              url: "",
              thumbnail: "",
            })
          }
          url={showVideo?.url}
          label={showVideo?.label}
          thumbnail={showVideo?.thumbnail}
        />
      ) : (
        ""
      )}
      {totalRecords > 0 && (
        <div className="guides-pagination-container">
          <PaginationComponent
            totalRecords={totalRecords}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
      <FilePreviewModal
        open={previewOpen}
        onClose={handleClosePreview}
        fileUrl={
          selectedFileForPreview?.url || selectedFileForPreview?.file?.url
        }
        fileName={selectedFileForPreview?.fileName}
        id={selectedFileForPreview?.id}
        previewLink={selectedFileForPreview?.previewLink}
        size={selectedFileForPreview?.file?.size}
      />
    </>
  );
};

export default GuidesList;
